import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { useAuth } from "../../contexts/AuthContext";
import Loading from "../../components/Loading";
import SaveButton from "../../components/buttons/SaveButton";
import CancelButton from "../../components/buttons/CancelButton";
import { partService } from "../../services/api";
import PartForm from "./PartForm";

import orderStyles from "../orders/OrderDetail.module.css";
import styles from "./PartDetail.module.css";

function formatHistoryFieldValue(key, value) {
  return value == null ? "—" : String(value);
}

function formatHistorySummary(entry) {
  const { action, changes } = entry;
  if (action === "create") {
    return "Part created";
  }
  if (!changes || typeof changes !== "object") {
    return "Updated";
  }
  return Object.entries(changes)
    .map(([key, val]) => {
      if (val && typeof val === "object" && "from" in val && "to" in val) {
        const from = formatHistoryFieldValue(key, val.from);
        const to = formatHistoryFieldValue(key, val.to);
        return `${key}: ${from} → ${to}`;
      }
      return key;
    })
    .join(" · ");
}

function formatHistoryTime(value) {
  if (!value) {
    return "";
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    return String(value);
  }
  return d.toLocaleString();
}

const META_SHORT_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function formatEur(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) {
    return "—";
  }
  return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(n);
}

function formatMetaDateTime(value) {
  if (!value) {
    return "";
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    return String(value);
  }
  const pad = (n) => String(n).padStart(2, "0");
  const day = d.getDate();
  const mon = META_SHORT_MONTHS[d.getMonth()];
  const year = d.getFullYear();
  return `${day}-${mon}-${year}, ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function PartDetail() {
  const { partId } = useParams();
  const { hasPermission } = useAuth();

  const [part, setPart] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saveError, setSaveError] = useState(null);

  const loadPart = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setNotFound(false);
      const res = await partService.getById(partId);
      setPart(res.data.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setNotFound(true);
        setPart(null);
      } else {
        setError(err.response?.data?.message || "Failed to load part");
        setPart(null);
      }
    } finally {
      setLoading(false);
    }
  }, [partId]);

  const loadHistory = useCallback(async () => {
    try {
      const res = await partService.getHistory(partId);
      setHistory(res.data.data || []);
    } catch {
      setHistory([]);
    }
  }, [partId]);

  useEffect(() => {
    loadPart();
    loadHistory();
  }, [loadPart, loadHistory]);

  const usedInOrders = useMemo(
    () => (Array.isArray(part?.usedInOrders) ? part.usedInOrders : []),
    [part?.usedInOrders]
  );

  /** Weighted average € per piece: sum(qty × unit price) / sum(qty) across order lines. */
  const usedOnOrdersAvgUnitPriceEur = useMemo(() => {
    let totalQty = 0;
    let totalValue = 0;
    for (const row of usedInOrders) {
      const q = Number(row.quantity);
      const lineVal = Number(row.lineTotalEur);
      const p = Number(row.unitPriceEur);
      const value = Number.isFinite(lineVal)
        ? lineVal
        : Number.isFinite(q) && Number.isFinite(p)
          ? Math.round(q * p * 10000) / 10000
          : NaN;
      if (Number.isFinite(value) && Number.isFinite(q) && q > 0) {
        totalValue += value;
        totalQty += q;
      }
    }
    if (totalQty <= 0 || !Number.isFinite(totalValue)) {
      return null;
    }
    return Math.round((totalValue / totalQty) * 10000) / 10000;
  }, [usedInOrders]);

  const beginEdit = () => {
    if (!part) {
      return;
    }
    setSaveError(null);
    setDraft({
      partNumber: part.partNumber ?? "",
      description: part.description ?? "",
      category: part.category ?? "",
      notes: part.notes ?? "",
    });
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setDraft(null);
    setSaveError(null);
  };

  const handleFieldChange = (field, value) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!draft || !part) {
      return;
    }
    if (!draft.partNumber?.trim()) {
      setSaveError("Part number is required");
      return;
    }
    setSaveError(null);
    try {
      await partService.update(part.id, {
        partNumber: draft.partNumber,
        description: draft.description,
        category: draft.category,
        notes: draft.notes || null,
      });
      await loadPart();
      await loadHistory();
      setEditing(false);
      setDraft(null);
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        (err.response?.status === 409 ? "Part number already in use" : "Failed to save");
      setSaveError(msg);
    }
  };

  if (loading) {
    return (
      <div className={orderStyles.container}>
        <Loading loadingText="Loading part..." />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className={orderStyles.container}>
        <div className={orderStyles.card}>
          <div className={orderStyles.notFound}>
            <p>Part not found.</p>
            <Link to="/parts" className={orderStyles.backLink}>
              Back to parts
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (error || !part) {
    return (
      <div className={orderStyles.container}>
        <div className={orderStyles.error}>{error || "Something went wrong."}</div>
        <Link to="/parts" className={orderStyles.backLink}>
          Back to parts
        </Link>
      </div>
    );
  }

  return (
    <div className={orderStyles.container}>
      <header className={`${orderStyles.orderPageHeader} ${styles.pageHeader}`}>
        <div className={styles.headerStack}>
          <h1 className={styles.partNumberTitle}>{part.partNumber}</h1>
          {part.description ? (
            <p className={styles.partDescriptionLead}>{part.description}</p>
          ) : null}
        </div>
      </header>

      <div className={orderStyles.card}>
        <div className={orderStyles.headerBar}>
          <Link to="/parts" className={orderStyles.backLink}>
            ← Back to parts
          </Link>
          <div className={orderStyles.actions}>
            {hasPermission("component_orders:update") && !editing && (
              <button type="button" className={orderStyles.editToggle} onClick={beginEdit}>
                Edit
              </button>
            )}
            {hasPermission("component_orders:update") && editing && (
              <>
                <SaveButton text="Save changes" onClick={handleSave} />
                <CancelButton onClick={cancelEdit} />
              </>
            )}
          </div>
        </div>

        <div className={orderStyles.body}>
          {saveError && <div className={orderStyles.error}>{saveError}</div>}

          {!editing ? (
            <dl className={orderStyles.detailGrid}>
              <div className={orderStyles.detailItem}>
                <dt>Part number</dt>
                <dd>{part.partNumber || "—"}</dd>
              </div>
              <div className={orderStyles.detailItem}>
                <dt>Description</dt>
                <dd>{part.description || "—"}</dd>
              </div>
              <div className={orderStyles.detailItem}>
                <dt>Category</dt>
                <dd>{part.category || "—"}</dd>
              </div>
              <div className={`${orderStyles.detailItem} ${orderStyles.detailItemFull}`}>
                <dt>Notes</dt>
                <dd className={orderStyles.remarksBody}>{part.notes || "—"}</dd>
              </div>
            </dl>
          ) : (
            <div className={orderStyles.editForm}>
              <h2 className={orderStyles.editTitle}>Edit part</h2>
              <PartForm values={draft} onFieldChange={handleFieldChange} idPrefix="part-detail" />
            </div>
          )}
        </div>
      </div>

      <div className={styles.ordersCard}>
        <h2 className={styles.ordersTitle}>Used on orders</h2>
        <p className={styles.ordersNote}>
          Quantities and <strong>unit prices (€ per piece)</strong> are taken from each order line.
          Line total is quantity × unit price.
        </p>
        {usedInOrders.length === 0 ? (
          <p className={styles.ordersEmpty}>This part is not linked to any order yet.</p>
        ) : (
          <>
            <div className={styles.ordersTableWrap}>
              <table className={styles.ordersDataTable}>
                <thead>
                  <tr>
                    <th>Order No.</th>
                    <th>Order part number</th>
                    <th>Order serial number</th>
                    <th>Customer</th>
                    <th>Qty</th>
                    <th>Unit price (€ / pc)</th>
                    <th>Line total (€)</th>
                  </tr>
                </thead>
                <tbody>
                  {usedInOrders.map((row) => (
                    <tr key={row.lineId}>
                      <td>
                        <Link className={styles.orderRowLink} to={`/orders/${row.orderId}`}>
                          {(row.internalNo && row.internalNo.trim()) || `Order #${row.orderId}`}
                        </Link>
                      </td>
                      <td>{row.orderPartNumber || "—"}</td>
                      <td>{row.serialNumber || "—"}</td>
                      <td>{row.customerName || "—"}</td>
                      <td>{row.quantity ?? "—"}</td>
                      <td>{formatEur(row.unitPriceEur)}</td>
                      <td>{formatEur(row.lineTotalEur)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className={styles.ordersTotalRow}>
                    <td colSpan={7} className={styles.ordersFooterSummary}>
                      <span className={styles.ordersFooterLabel}>
                        Average price per single unit (weighted by quantity)
                      </span>
                      <span className={styles.ordersFooterValue}>
                        {usedOnOrdersAvgUnitPriceEur == null ? "—" : formatEur(usedOnOrdersAvgUnitPriceEur)}
                      </span>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </>
        )}
      </div>

      <div className={orderStyles.historyCard}>
        <h2 className={orderStyles.historyTitle}>History</h2>
        <div className={orderStyles.historyOrderMeta}>
          <span>Created by {part.createdBy || "—"}</span>
          {" · "}
          <span>Updated by {part.updatedBy || "—"}</span>
          {part.createdAt && (
            <>
              {" · "}
              <span>Created {formatMetaDateTime(part.createdAt)}</span>
            </>
          )}
          {part.updatedAt && (
            <>
              {" · "}
              <span>Updated {formatMetaDateTime(part.updatedAt)}</span>
            </>
          )}
        </div>
        {history.length === 0 ? (
          <p className={orderStyles.historySummary}>No history entries yet.</p>
        ) : (
          <ul className={orderStyles.historyList}>
            {history.map((entry) => (
              <li key={entry.id} className={orderStyles.historyItem}>
                <div className={orderStyles.historyLeading}>
                  <span>{formatHistoryTime(entry.createdAt)}</span>
                  <span className={orderStyles.historySep}>·</span>
                  <span>{entry.actorUsername}</span>
                  <span className={orderStyles.historySep}>·</span>
                  <span className={orderStyles.historyAction}>{entry.action}</span>
                </div>
                <span className={orderStyles.historySep}>·</span>
                <span className={orderStyles.historySummary}>{formatHistorySummary(entry)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default PartDetail;
